import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Flex, Button } from '@electricjs/arc';

import { ConfirmedOrder } from '@/types/orders';
import { IT_HUB_ORIGIN, IT_HUB_EMBEDDED_ORIGIN } from '@/constants/common';
import { StorefrontSteps } from '@/types/storefront';
import { usePlaceOrderMutation } from '@/redux/slices/orderApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import {
  usePostMessageToParent,
  EmbeddedStorefrontMessageType,
} from '@/hooks/postMessageToParent';
import { getBaseQueryErrorMessage } from '@/redux/slices/helpers/errorHandlersHelpers';

import OrderSummary from './Summary/OrderSummary';
import CartSummary from './Summary/CartSummary';
import QuickActions from './Summary/QuickActions';
import { useStorefront } from '../StorefrontProvider';
import { hasAnyAppleDeviceEntry } from '../utils/helpers';
import OrderSubmitModal from './OrderSubmitModal';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import useIsABMStatusUnknown from '@/hooks/storefront/useIsABMStatusUnknown';
import useIsCaResellerUnconfirmed from '@/hooks/storefront/useIsCaResellerUnconfirmed';

export const RightPanelContainer = styled(Flex)<{ $isStacked?: boolean }>`
  z-index: 999;
  top: 0;
  padding-top: 2rem;
  row-gap: 2rem;
  flex-direction: column;
  position: sticky;
  max-width: 46rem; // caps the panel's width when it sits beside the Left Panel content, so it doesn't stretch as wide as the leftover space
  min-width: 40rem;
  flex-shrink: 0; // needed when it's used inside QuoteCheckout (it's a direct descendent of a Flex container)
  ${({ $isStacked }) =>
    $isStacked &&
    css`
      /* when stacked above the Left Panel content, it should fill the row's full width instead of being capped */
      position: static;
      z-index: auto;
      max-width: none;
      min-width: 0;
    `}
`;

type RightPanelProps = {
  hasBillingInfo?: boolean;
  isStacked?: boolean;
};

const RightPanel = ({ hasBillingInfo, isStacked }: RightPanelProps) => {
  const { t } = useTranslation('storefront');
  const {
    storefrontStep,
    setStorefrontStep,
    itemsInCart,
    assetCart,
    catalogCart,
    hasOnlyAssetsInCart,
    cartRequiresShippingAddress,
    storefrontCheckoutForm,
    orderKey,
    regenerateOrderKey,
  } = useStorefront();

  const organizationId = useGetOrganizationId();

  const isAbmStatusUnknown = useIsABMStatusUnknown();

  const selectedShoppingCountry =
    storefrontCheckoutForm?.watch('shoppingCountry');
  const isCaResellerUnconfirmed = useIsCaResellerUnconfirmed(
    selectedShoppingCountry?.value
  );

  const [placeOrder] = usePlaceOrderMutation(); // loading and errors are handled by the function and its promise

  const { openModal, closeModal, showErrorToast } = useGlobalUI();

  const postMessageToParent = usePostMessageToParent();

  const handleCartPageContinueButton = () => {
    if (!cartRequiresShippingAddress) {
      openModal(<OrderSubmitModal />); // open the "loading" modal

      // place the "asset only" order
      placeOrder({
        orderIdempotentKey: orderKey,
        organizationId,
        orderOrigin,
        selectedEmployeeId:
          storefrontCheckoutForm?.getValues('employee')?.value ?? '',
        selectedAssets: assetCart,
      })
        .unwrap()
        .then((order: ConfirmedOrder | void) => {
          setStorefrontStep(StorefrontSteps.ORDER_PLACED); // go to confirmation page
          closeModal();

          // Post purchase complete message to parent frame (hook handles embedded check)
          postMessageToParent({
            type: EmbeddedStorefrontMessageType.PURCHASE_COMPLETE,
            orderId: order?.friendlyId,
            timestamp: new Date().toISOString(),
          });
        })
        .catch(error => {
          console.log('Error submitting order', error);
          const errorMessage = getBaseQueryErrorMessage(error);

          closeModal();
          showErrorToast({
            id: 'order-submit-error-toast',
            title: t('steps.checkout.errors.submitTitle'),
            message: errorMessage,
            hideAfter: 5000,
            stack: true,
          });
        })
        .finally(() => regenerateOrderKey());
    } else {
      // Block advancing to checkout while Windows Autopilot is enabled but no
      // Microsoft tenant has been selected.
      if (storefrontCheckoutForm) {
        const { wAutopilotEnabled, wAutopilotMsTenantId } =
          storefrontCheckoutForm.getValues();

        if (wAutopilotEnabled && !wAutopilotMsTenantId) {
          storefrontCheckoutForm.setError('wAutopilotMsTenantId', {
            type: 'manual',
            message: t('wautopilot.wautopilotSwitch.tenantRequired'),
          });
          return;
        }

        storefrontCheckoutForm.clearErrors('wAutopilotMsTenantId');
      }

      setStorefrontStep(StorefrontSteps.CHECKOUT);
    }
  };

  const { isEmbedded } = useIsEmbedded();
  const placeOrderText = isEmbedded
    ? t('rightPanel.placeOrderEmbedded')
    : t('rightPanel.placeOrder');
  const orderOrigin = isEmbedded ? IT_HUB_EMBEDDED_ORIGIN : IT_HUB_ORIGIN;

  return (
    <RightPanelContainer $isStacked={isStacked}>
      <OrderSummary />
      {storefrontStep !== StorefrontSteps.CART && (
        <CartSummary
          hasEditButton={storefrontStep !== StorefrontSteps.ORDER_PLACED}
        />
      )}
      {/* Checkout Button */}
      {storefrontStep === StorefrontSteps.CART && (
        <Button
          id="cart-page-continue-button"
          disabled={
            !itemsInCart ||
            (!hasBillingInfo && !hasOnlyAssetsInCart) ||
            (isAbmStatusUnknown && hasAnyAppleDeviceEntry(catalogCart)) ||
            (isCaResellerUnconfirmed && hasAnyAppleDeviceEntry(catalogCart))
          }
          width="100%"
          p="2rem"
          onClick={handleCartPageContinueButton}>
          {!cartRequiresShippingAddress
            ? placeOrderText
            : t('rightPanel.continueToCheckout')}
        </Button>
      )}
      {storefrontStep === StorefrontSteps.ORDER_PLACED && <QuickActions />}
    </RightPanelContainer>
  );
};

export default RightPanel;
